import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { RootRoute } from "@app/const";
import DesignerSetting from '@app/DesignerSetting';
import { AgenticPage } from "@app/pages/app/agentic/page";
import { IntentLayout } from "@app/pages/app/intent/layout";
import { SettingsLayout } from '@app/pages/app/settings/layout';
import Utility from "@app/utilities/Utility";
import { ChangeLog } from "@app/widgets";
import DesignerVersionWidget from "@app/widgets/DesignerVersionWidget";
import CommonModuleDefaultsDialog from '@app/widgets/dialogs/CommonModuleDefaultsDialog';
import DisabledInFlowDialog from '@app/widgets/dialogs/DisabledInFlowDialog';
import ValidatorDialog from "@app/widgets/dialogs/ValidatorDialog";
import IntentsWidget from "@app/widgets/IntentsWidget";
import SaveWidget from "@app/widgets/SaveWidget";
import TestToolWidget from "@app/widgets/TestToolWidget";

/**
 * Handles all rendering of Application React Components via ReactDOM.render
 */
export class ReactDOMComponents {
    get settingsLayout() {
        return (
            <BrowserRouter basename="/app/settings">
                <SettingsLayout />
            </BrowserRouter>
        );
    }

    get intentLayout() {
        // Intents need IntentsWidget as a legacy shell for the property-panel
        // lifecycle (document listeners in IntentsWidget.js). Settings has no
        // equivalent wrapper because routed pages mount directly under #settings.
        return (
            <IntentsWidget>
                <BrowserRouter basename="/app/intent">
                    <IntentLayout />
                </BrowserRouter>
            </IntentsWidget>
        );
    }

    renderApplicationPages() {
        this.render('version');
        this.render(RootRoute.agentic);
        this.render(RootRoute.intents);
        this.render(RootRoute.settings);
        this.render(RootRoute.save);
        this.render(RootRoute.testTool);
    }

    /**
     * @param {RootRoute | 'changeLog' | 'version'} routeOrArea to render.
     */
    render(routeOrArea) {
        const {
            changeLogMaps,
            latestVersion,
            serialized
        } = window.apm;
        const isCommonModule = Utility.getIsCommonModule(serialized);
        const isClientView = DesignerSetting.s.is_public === "true";
        /** @param {RootRoute} route */
        const getPageHostSelector = (route) => {
            // Scope under #app-main: jstree library folders reuse ids like `intents`.
            return `#app-main > #${route}`;
        };

        const { selector, element } = (() => {
            switch (routeOrArea) {
                case 'changeLog':
                    return {
                        element: (
                            <ChangeLog
                                changeLogs={changeLogMaps}
                                serialized={serialized}
                                // Fallback for offline mode.
                                version={latestVersion ?? serialized.version}
                            />
                        ),
                        selector: '#changelogs-container'
                    };
                case 'version':
                    return {
                        element: <DesignerVersionWidget />,
                        selector: ".designer-version-container"
                    };
                case RootRoute.agentic:
                    return (isClientView || isCommonModule) ? {} : {
                        element: <AgenticPage />,
                        selector: getPageHostSelector(routeOrArea)
                    };
                case RootRoute.intents:
                    return (isCommonModule) ? {} : {
                        element: this.intentLayout,
                        selector: getPageHostSelector(routeOrArea)
                    };
                case RootRoute.save:
                    return (isClientView) ? {} : {
                        element: (
                            <SaveWidget
                                labels={window.l.save}
                                serialized={serialized}
                            />
                        ),
                        selector: getPageHostSelector(routeOrArea)
                    };
                case RootRoute.settings:
                    return {
                        element: this.settingsLayout,
                        selector: getPageHostSelector(routeOrArea)
                    };
                case RootRoute.testTool:
                    return (isClientView || isCommonModule) ? {} : {
                        element: <TestToolWidget serialized={serialized} />,
                        selector: getPageHostSelector(routeOrArea)
                    };
                default:
                    throw new Error(`Unable to render unknown route or area '${routeOrArea}'.`);
            }
        })();

        const container = (!selector) ? null : document.querySelector(selector);
        if (!container || !element) {
            return;
        }

        // Only BrowserRouter-wrapped widgets need a full remount to read the current URL;
        // unmounting the others (e.g. SaveWidget) would drop input focus on every re-render.
        if (routeOrArea === RootRoute.settings || routeOrArea === RootRoute.intents) {
            ReactDOM.unmountComponentAtNode(container);
        }
        ReactDOM.render(element, container);
    }

    renderDialog({ name, props, $el }) {
        switch (name) {
            case 'changeLogDialog':
                ReactDOM.render(
                    <ChangeLog {...props} />,
                    $($el).get(0)
                );
                break;
            case 'validatorDialog':
                ReactDOM.render(
                    <ValidatorDialog {...props} />,
                    $($el).get(0));
                break;
            case 'disabledItemsDialog':
                ReactDOM.render(
                    <DisabledInFlowDialog {...props} />,
                    $($el).get(0));
                break;
            case 'setCommonModuleDefaults':
                ReactDOM.render(
                    <CommonModuleDefaultsDialog {...props} />,
                    $($el).get(0));
                break;
            default:
                break;
        }
    }

    unmountComponentAtNode($el) {
        ReactDOM.unmountComponentAtNode($($el).get(0));
    }
}
